/*
 * Copyright (C) 2023 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Location } from '@angular/common';
import { PortfolioModel } from '@app/app/shared/models/portfolio/portfolio.model';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, switchMap } from 'rxjs';
import { LanguageCodeHelper } from 'src/app/shared/helpers/language-code-helper';
import { Page } from 'src/app/shared/models/page';
import { FrameworkTrackerSectionTag, SectionReorder } from 'src/app/shared/models/page/page.model';
import { RestClientService } from 'src/app/shared/services/rest-client.service';
import { ObservableResult } from 'src/app/shared/store';
import { environment } from 'src/environments/environment';
import { FolioProject } from '../../project/models/project.model';
import { JobEntity } from '../components/view/shared/jobs-section/jobs.model';
import { Moment } from '../models';
import {
  AchievementCertificateBlock,
  FolioBadges,
  FolioCoverUpdateRequest,
  FolioModel,
  FolioMyActivity,
  FolioSection,
  FolioSectionItemRequest,
  FolioSectionRequest,
  FolioShowcaseRequest,
  MemberFolioBadgeItem,
  PublicFolioBadge,
} from '../store/folio.state.model';
import { FolioDataService } from './data.service';

export class ApiFolioDataService implements FolioDataService {
  constructor(
    private client: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  getUserFolio(languageCode?: string): ObservableResult<FolioModel> {
    return this.client
      .get<FolioModel>(
        Location.joinWithSlash(environment.apiRootUrl || '', 'folios'),
        null,
        languageCode ? { 'Accept-Language': languageCode } : null,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetFolio'))),
      );
  }

  updateUserFolioShowcase(request: FolioShowcaseRequest): ObservableResult<void> {
    return this.client.post<void>(Location.joinWithSlash(environment.apiRootUrl || '', 'folios/showcases'), request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateFolioStatus'))),
    );
  }

  updateUserFolioCoverImage(request: FolioCoverUpdateRequest): ObservableResult<{ uploadUrl: string }> {
    return this.client
      .post<{ uploadUrl: string }>(Location.joinWithSlash(environment.apiRootUrl || '', 'folios/cover/image'), request)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateFolioStatus'))),
      );
  }

  deleteUserFolioCoverImage(): ObservableResult<void> {
    return this.client.delete<void>(Location.joinWithSlash(environment.apiRootUrl || '', 'folios/cover/image')).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateFolioStatus'))),
    );
  }

  reorderFolioSections(request: SectionReorder): ObservableResult<void> {
    return this.client.post<void>(Location.joinWithSlash(environment.apiRootUrl || '', 'folios/reorder'), request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateFolioSection'))),
    );
  }

  updateFolioSection(sectionUid: string, request: FolioSectionRequest, languageCode?: string): ObservableResult<FolioSection> {
    return this.client
      .post<FolioSection>(
        Location.joinWithSlash(environment.apiRootUrl || '', `folios/sections/${sectionUid}`),
        request,
        languageCode ? LanguageCodeHelper.checkAndGetContentLanguageCode(languageCode) : null,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateFolioSection'))),
      );
  }

  enableFolioSectionItemVisibility(sectionUid: string, request: FolioSectionItemRequest): ObservableResult<void> {
    return this.client
      .patch<void>(
        Location.joinWithSlash(environment.apiRootUrl || '', `folios/visibilities/sections/${sectionUid}/${request.type}/${request.uid}`),
        request,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateFolioSection'))),
      );
  }

  disableFolioSectionItemVisibility(sectionUid: string, request: FolioSectionItemRequest): ObservableResult<void> {
    return this.client
      .delete<void>(
        Location.joinWithSlash(environment.apiRootUrl || '', `folios/visibilities/sections/${sectionUid}/${request.type}/${request.uid}`),
        null,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateFolioSection'))),
      );
  }

  deleteCertificate(uid: string): ObservableResult<void> {
    return this.client
      .delete<void>(Location.joinWithSlash(environment.apiRootUrl || '', `folios/achievements/certificates/${uid}`), null)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateFolioSection'))),
      );
  }

  addMomentToFolioSection(sectionUid: string, momentUid: string): ObservableResult<void> {
    return this.client
      .patch<void>(Location.joinWithSlash(environment.apiRootUrl || '', `folios/sections/${sectionUid}/moments/${momentUid}`), null)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorAddMomentToFolio'))),
      );
  }

  addTagToFolioSection(sectionUid: string, tagUid: string): ObservableResult<void> {
    return this.client
      .patch<void>(Location.joinWithSlash(environment.apiRootUrl || '', `folios/sections/${sectionUid}/tags/${tagUid}`), null)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorAddGrowthToFolio'))),
      );
  }

  removeMomentFromFolioSection(sectionUid: string, momentUid: string): ObservableResult<void> {
    return this.client
      .delete<void>(Location.joinWithSlash(environment.apiRootUrl || '', `folios/sections/${sectionUid}/moments/${momentUid}`), null)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorRemoveMomentFromFolio'))),
      );
  }

  removeTagFromFolioSection(sectionUid: string, tagUid: string): ObservableResult<void> {
    return this.client
      .delete<void>(Location.joinWithSlash(environment.apiRootUrl || '', `folios/sections/${sectionUid}/tags/${tagUid}`), null)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorRemoveGrowthFromFolio'))),
      );
  }

  getUserFolioProjects(folioPublicId: string, page: number, size: number = 15): ObservableResult<Page<FolioProject>> {
    return this.client
      .get<Page<FolioProject>>(Location.joinWithSlash(environment.apiRootUrl || '', `projects/folio/${folioPublicId}`), {
        page: page.toString(),
        size: size.toString(),
      })
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetProjects'))),
      );
  }

  getFolioProjects(page: number, size: number = 4): ObservableResult<Page<FolioProject>> {
    return this.client
      .get<Page<FolioProject>>(Location.joinWithSlash(environment.apiRootUrl || '', 'projects/folio-sections'), {
        page: page.toString(),
        size: size.toString(),
      })
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetProjects'))),
      );
  }

  getFolioMoments(sectionUid: string, page: number, size: number): ObservableResult<Page<Moment>> {
    return this.client
      .get<Page<Moment>>(Location.joinWithSlash(environment.apiRootUrl || '', `moments/folio-sections/${sectionUid}`), {
        page: page.toString(),
        size: size ? size.toString() : '10',
      })
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetMoments'))),
      );
  }

  getFolioActivities(
    page: number,
    size: number,
    order: 'ASC' | 'DESC',
    dataRange: string,
    tags: string[] | null,
  ): ObservableResult<Page<FolioMyActivity>> {
    let queryParams = `?page=${page}&size=${size}&order=${order}`;

    if (dataRange) {
      queryParams += '&' + dataRange;
    }
    if (tags?.length > 0) {
      tags.forEach((tag) => {
        queryParams += '&tags=' + tag;
      });
    }
    return this.client.get<Page<FolioMyActivity>>(Location.joinWithSlash(environment.apiRootUrl || '', 'activities' + queryParams)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetFolioMyActivities'))),
    );
  }

  getFolioSkillsActivities(page: number, size: number, tagId: string): ObservableResult<Page<FolioMyActivity>> {
    const params = {
      page: page.toString(),
      size: size ? size.toString() : '10',
    };
    return this.client
      .get<Page<FolioMyActivity>>(Location.joinWithSlash(environment.apiRootUrl || '', `activities/skills/${tagId}`), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetFolioMyActivities'))),
      );
  }

  getFolioGrowths(sectionUid: string, page: number, size: number): ObservableResult<Page<FrameworkTrackerSectionTag>> {
    return this.client
      .get<Page<FrameworkTrackerSectionTag>>(Location.joinWithSlash(environment.apiRootUrl || '', `tags/folio-sections/${sectionUid}`), {
        page: page.toString(),
        size: size.toString(),
      })
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetFolioGrowths'))),
      );
  }

  getFolioBadges(sectionUid: string, page: number, size: number): ObservableResult<FolioBadges[]> {
    return this.client
      .get<FolioBadges[]>(Location.joinWithSlash(environment.apiRootUrl || '', `badges/folio-sections/${sectionUid}`), {
        page: page.toString(),
        size: size ? size.toString() : '10',
      })
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetBadges'))),
      );
  }

  /**
   * Getting public folio by publicId.
   */
  getPublicUserFolio(publicId: string, languageCode?: string): ObservableResult<FolioModel> {
    return this.client
      .get<FolioModel>(
        Location.joinWithSlash(environment.apiRootUrl || '', `folios/public/${publicId}`),
        null,
        languageCode ? { 'Accept-Language': languageCode } : null,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError((err) => {
          let errorMessage = this.translocoService.translate('translations.errors.errorGetFolio');
          if (err?.status === 404) {
            errorMessage = this.translocoService.translate('translations.errors.errorPublicFolioNotExists');
          } else if (err?.status === 403) {
            errorMessage = this.translocoService.translate('translations.errors.errorPublicFolioHidden');
          }
          return ObservableResult.ofError(errorMessage);
        }),
      );
  }

  getMemberFolio(memberId: string, languageCode?: string): ObservableResult<FolioModel> {
    return this.client
      .get<FolioModel>(
        Location.joinWithSlash(environment.apiRootUrl || '', `folios/by-member/${memberId}`),
        null,
        languageCode ? { 'Accept-Language': languageCode } : null,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError((err) => {
          let errorMessage = this.translocoService.translate('translations.errors.errorGetFolio');
          if (err?.status === 404) {
            errorMessage = "This member doesn't have a folio yet.";
          } else if (err?.status === 403) {
            errorMessage = this.translocoService.translate('translations.errors.errorPublicFolioHidden');
          }
          return ObservableResult.ofError(errorMessage);
        }),
      );
  }

  getMemberFolioSkills(memberId: string, page: number, size: number): ObservableResult<Page<FrameworkTrackerSectionTag>> {
    return this.client
      .get<Page<FrameworkTrackerSectionTag>>(
        Location.joinWithSlash(environment.apiRootUrl || '', `tags/by-member/${memberId}/skill-scores`),
        {
          page: page.toString(),
          size: size.toString(),
        },
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetFolioGrowths'))),
      );
  }

  // Frontend is ready; once BE ships it, the employer skills drawer will start showing the member's activities.
  getMemberFolioSkillsActivities(memberId: string, page: number, size: number, tagId: string): ObservableResult<Page<FolioMyActivity>> {
    const params = {
      page: page.toString(),
      size: size ? size.toString() : '10',
    };
    return this.client
      .get<
        Page<FolioMyActivity>
      >(Location.joinWithSlash(environment.apiRootUrl || '', `activities/by-member/${memberId}/skills/${tagId}`), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetFolioMyActivities'))),
      );
  }

  getMemberFolioCertificates(memberId: string): ObservableResult<AchievementCertificateBlock[]> {
    return this.client
      .get<AchievementCertificateBlock[]>(Location.joinWithSlash(environment.apiRootUrl || '', `folios/by-member/${memberId}/certificates`))
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body || [])),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetBadges'))),
      );
  }

  getMemberFolioBadges(memberId: string): ObservableResult<MemberFolioBadgeItem[]> {
    return this.client
      .get<MemberFolioBadgeItem[]>(Location.joinWithSlash(environment.apiRootUrl || '', `folios/by-member/${memberId}/badges`))
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body || [])),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetBadges'))),
      );
  }

  getPublicFolioGrowths(
    publicId: string,
    sectionUid: string,
    page: number,
    size: number,
  ): ObservableResult<Page<FrameworkTrackerSectionTag>> {
    return this.client
      .get<Page<FrameworkTrackerSectionTag>>(
        Location.joinWithSlash(environment.apiRootUrl || '', `tags/public/folios/${publicId}/folio-sections/${sectionUid}`),
        {
          page: page.toString(),
          size: size.toString(),
        },
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetFolioGrowths'))),
      );
  }

  getPublicFolioMoments(publicId: string, sectionUid: string, page: number, size: number): ObservableResult<Page<Moment>> {
    return this.client
      .get<Page<Moment>>(
        Location.joinWithSlash(environment.apiRootUrl || '', `moments/public/folios/${publicId}/folio-sections/${sectionUid}`),
        {
          page: page.toString(),
          size: size ? size.toString() : '10',
        },
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetMoments'))),
      );
  }

  getPublicFolioProjects(publicId: string, page: number, size: number): ObservableResult<Page<FolioProject>> {
    return this.client
      .get<Page<FolioProject>>(Location.joinWithSlash(environment.apiRootUrl || '', `projects/public/folios/${publicId}/folio-sections`), {
        page: page.toString(),
        size: size ? size.toString() : '4',
      })
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetProjects'))),
      );
  }

  getPublicFolioBadges(publicId: string, sectionUid: string, page: number, size: number): ObservableResult<FolioBadges[]> {
    return this.client
      .get<FolioBadges[]>(
        Location.joinWithSlash(environment.apiRootUrl || '', `badges/public/folios/${publicId}/folio-sections/${sectionUid}`),
        {
          page: page.toString(),
          size: size ? size.toString() : '10',
        },
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetBadges'))),
      );
  }

  getPublicFolioCertificates(
    publicId: string,
    sectionUid: string,
    page: number,
    size: number,
  ): ObservableResult<Page<AchievementCertificateBlock>> {
    return this.client
      .get<Page<AchievementCertificateBlock>>(
        Location.joinWithSlash(environment.apiRootUrl || '', `certificates/public/folios/${publicId}/folio-sections/${sectionUid}`),
        {
          page: page.toString(),
          size: size ? size.toString() : '10',
        },
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetBadges'))),
      );
  }

  getFolioUserPublicBadge(userBadgeId: string): ObservableResult<PublicFolioBadge> {
    return this.client.get<PublicFolioBadge>(Location.joinWithSlash(environment.apiRootUrl || '', `badges/issued/${userBadgeId}`)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetBadge'))),
    );
  }

  getFolioJobs(page: number, languageCode?: string): ObservableResult<JobEntity[]> {
    return this.client
      .get<JobEntity[]>(
        Location.joinWithSlash(environment.apiRootUrl || '', '/jobs/retrieve'),
        {
          page: page.toString(),
        },
        languageCode ? LanguageCodeHelper.checkAndGetContentLanguageCode(languageCode) : null,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetBadges'))),
      );
  }

  getUserPortfolios(page: number, size: number): ObservableResult<Page<PortfolioModel>> {
    return this.client
      .get<Page<PortfolioModel>>(Location.joinWithSlash(environment.apiRootUrl || '', `portfolios/search`), {
        page: page.toString(),
        pageSize: size ? size.toString() : '4',
      })
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetFolio'))),
      );
  }

  getUserPublicPortfolios(publicId: string, page: number, size: number): ObservableResult<Page<PortfolioModel>> {
    return this.client
      .get<Page<PortfolioModel>>(Location.joinWithSlash(environment.apiRootUrl || '', `portfolios/public/search`), {
        page: page.toString(),
        pageSize: size ? size.toString() : '4',
        publicId: publicId,
      })
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetFolio'))),
      );
  }

  likeMoment(momentUid: string): ObservableResult<number> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `moments/${momentUid}/likes`);
    return this.client.post<number>(url, undefined).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorLikingMoment'))),
    );
  }

  unlikeMoment(momentUid: string): ObservableResult<number> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `moments/${momentUid}/likes`);
    return this.client.delete<number>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUnlikingMoment'))),
    );
  }

  getMoments(
    context: 'community' | 'folio',
    page: number,
    size: number,
    sort?: string,
    tags?: string[],
    publicId?: string,
    sectionUid?: string,
  ): ObservableResult<Page<Moment>> {
    if (context === 'community') {
      return this.getCommunityMoments(page, size, sort, tags);
    }

    if (publicId && sectionUid) {
      return this.getPublicFolioMoments(publicId, sectionUid, page, size);
    } else if (sectionUid) {
      return this.getFolioMoments(sectionUid, page, size);
    } else {
      throw new Error('sectionUid is required for folio moments');
    }
  }

  getCommunityMoments(page: number, size: number, sort?: string, tags?: string[]): ObservableResult<Page<Moment>> {
    const params: { [key: string]: string } = {
      page: page.toString(),
      pageSize: size ? size.toString() : '10',
    };

    if (sort) {
      params.sort = sort;
    }

    if (tags && tags.length > 0) {
      tags.forEach((tag) => {
        params[`tags`] = tag;
      });
    }

    return this.client.get<Page<Moment>>(Location.joinWithSlash(environment.apiRootUrl || '', `moments/search/organization`), params).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetMoments'))),
    );
  }
}
